/**
 * AiEstimationLeadSection
 * =======================
 *
 * Read-only display of an AI Estimation lead's answers + uploaded photos
 * in the lead detail page. Rendered only when `lead.form_type === 'ai_estimation'`.
 *
 * Translates the stable preset keys (work_type, surface_range, etc.) into
 * the original human-readable French labels via a lookup table. New presets
 * can append to PRESET_LABELS without touching this component's render path.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Image as ImageIcon, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

// Pavé Uni preset — keep in sync with backend config/ai_estimation_presets.py
const PAVE_UNI_LABELS = {
  steps: {
    work_type: 'Type de travaux',
    location: 'Emplacement principal',
    surface_range: 'Surface approximative',
    condition: 'État du pavé / affaissement',
    access_difficulty: "Difficulté d'accès",
    additional_elements: 'Éléments additionnels',
    timeline: 'Délai souhaité',
    project_notes: 'Précisions du client',
    contact: 'Coordonnées',
    photos: 'Photos',
  },
  values: {
    // work_type
    new_install: 'Nouvelle installation',
    repair: 'Réparation / rénovation',
    full_replacement: 'Remplacement complet',
    not_sure: 'Je ne suis pas certain',
    // location
    driveway: 'Entrée de garage',
    commercial_parking: 'Stationnement commercial',
    backyard: 'Cour arrière',
    patio: 'Patio / terrasse',
    pool_surround: 'Contour de piscine',
    sidewalk: 'Trottoir',
    retaining_wall: 'Muret',
    other: 'Autre',
    // surface_range
    lt_100: 'Moins de 100 pi²',
    '100_250': '100 à 250 pi²',
    '250_500': '250 à 500 pi²',
    '500_1000': '500 à 1 000 pi²',
    '1000_2000': '1 000 à 2 000 pi²',
    gt_2000: 'Plus de 2 000 pi²',
    unknown: 'Inconnu',
    // condition
    no_pavers: 'Aucun pavé existant',
    good: 'Pavé en bon état',
    minor_sinking: 'Affaissements légers',
    major_sinking: 'Affaissements importants',
    severely_damaged: 'Très détérioré',
    // access_difficulty
    easy: 'Accès facile',
    medium: 'Accès moyen',
    hard: 'Accès difficile',
    // additional_elements
    pool_nearby: 'Piscine à proximité',
    borders: 'Bordures',
    stairs: 'Escaliers',
    lighting: 'Éclairage',
    drainage: 'Drainage',
    none: 'Aucun',
    // timeline
    urgent: 'Le plus tôt possible (urgent)',
    '1_3_months': 'Dans 1 à 3 mois',
    '3_6_months': 'Dans 3 à 6 mois',
    this_year: 'Cette année',
    next_year: "L'an prochain",
    estimate_only: 'Estimation seulement',
  },
};

// Fissures & Drains preset — keep in sync with backend config/ai_estimation_presets.py
const FONDATION_DRAIN_LABELS = {
  steps: {
    main_need: 'Besoin principal',
    crack_location: 'Emplacement de la fissure',
    water_infiltration: "Infiltration d'eau",
    crack_count: 'Nombre de fissures',
    crack_excavator_access: 'Accès mini-excavatrice (fissure)',
    excavation_method: "Méthode d'excavation",
    drain_service: 'Service demandé',
    drain_symptoms: 'Symptômes observés',
    drain_age: 'Âge du drain',
    drain_obstacles: 'Obstacles au-dessus du drain',
    camera_access: 'Accès caméra existant',
    camera_excavation_method: "Création de l'accès caméra",
    section_location: 'Emplacement de la section',
    section_length_ft: 'Longueur de la section (pi)',
    section_depth_ft: 'Profondeur (pi)',
    section_excavator_access: 'Accès mini-excavatrice (section)',
    approx_linear_feet: 'Longueur du drain (pi lin.)',
    project_notes: 'Précisions du client',
    contact: 'Coordonnées',
    photos: 'Photos',
  },
  values: {
    fissure_repair: "Réparation d'une fissure de fondation",
    drain_francais: 'Inspection ou travaux de drain français',
    interior: "Visible à l'intérieur",
    exterior: "Accessible par l'extérieur",
    both: "Visible à l'intérieur et à l'extérieur",
    unknown: 'Je ne sais pas',
    active: "Oui, de l'eau entre présentement",
    traces: "Traces d'infiltration, aucune eau présentement",
    no_water: 'Non',
    one: 'Une',
    two: 'Deux',
    three_plus: 'Trois ou plus',
    yes: 'Oui',
    no: 'Non',
    mechanical: 'Excavation mécanique',
    manual: 'Excavation manuelle',
    both_options: 'Voir les deux options',
    advise_me: 'Conseillez-moi',
    camera_inspection: 'Inspection par caméra',
    section_repair: "Réparation d'une section",
    full_replacement: 'Remplacement complet du drain français',
    water_basement: 'Eau au sous-sol présentement',
    humidity_walls: 'Humidité au bas des murs',
    efflorescence: 'Efflorescence blanche sur les murs',
    mold_odor: "Moisissures ou odeur d'humidité",
    sump_pump_often: 'Pompe de puisard qui fonctionne souvent',
    old_drain: 'Drain ancien ou jamais inspecté',
    preventive: 'Aucun symptôme, inspection préventive',
    lt_10: 'Moins de 10 ans',
    '10_20': '10 à 20 ans',
    '20_30': '20 à 30 ans',
    gt_30: 'Plus de 30 ans',
    no_known_drain: 'Aucun drain connu',
    balcony: 'Balcon ou terrasse',
    pavers: 'Pavé uni',
    concrete_slab: 'Trottoir ou dalle de béton',
    ac_heatpump: 'Climatiseur ou thermopompe',
    stairs: 'Escaliers',
    none: 'Aucun',
    front: 'Avant',
    back: 'Arrière',
    side: 'Côté',
  },
};

const PRESETS = {
  pave_uni: PAVE_UNI_LABELS,
  fondation_drain: FONDATION_DRAIN_LABELS,
};

const PRESET_NAMES = {
  pave_uni: 'Pavé Uni',
  fondation_drain: 'Fissures & Drains',
};

// Display order of answers per preset (contact & photos rendered separately).
const PRESET_ORDERED_KEYS = {
  pave_uni: [
    'work_type', 'location', 'surface_range', 'condition',
    'access_difficulty', 'additional_elements', 'timeline', 'project_notes',
  ],
  fondation_drain: [
    'main_need', 'crack_location', 'water_infiltration', 'crack_count',
    'crack_excavator_access', 'excavation_method', 'drain_service',
    'drain_symptoms', 'drain_age', 'drain_obstacles', 'camera_access',
    'camera_excavation_method', 'section_location', 'section_length_ft',
    'section_depth_ft', 'section_excavator_access', 'approx_linear_feet',
    'project_notes',
  ],
};

function labelStep(presetId, key) {
  return PRESETS[presetId]?.steps?.[key] || key;
}

function labelValue(presetId, value) {
  if (value == null) return '—';
  if (Array.isArray(value)) {
    return value.map((v) => PRESETS[presetId]?.values?.[v] || v).join(', ') || '—';
  }
  if (typeof value === 'object') return null;
  return PRESETS[presetId]?.values?.[value] || String(value);
}

export function AiEstimationLeadSection({ lead, language }) {
  const presetId = lead.ai_estimation_preset || 'pave_uni';
  const answers = lead.ai_estimation_answers || {};
  const contact = answers.contact || {};
  const photoIds = lead.ai_estimation_photo_ids || [];

  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const fetchPhotos = async () => {
      if (!lead.lead_id) return;
      try {
        const { data } = await api.get(`/photos/list/lead/${lead.lead_id}`);
        if (!cancelled) setPhotos(data?.photos || []);
      } catch {
        /* photos are optional — silent fail */
      }
    };
    fetchPhotos();
    return () => { cancelled = true; };
  }, [lead.lead_id, photoIds.length]);
  const photoCount = Math.max(photos.length, photoIds.length);

  // Build ordered rows: skip "contact" (rendered separately), skip "photos" (rendered separately).
  const orderedKeys = PRESET_ORDERED_KEYS[presetId] || PRESET_ORDERED_KEYS.pave_uni;
  const rows = orderedKeys
    .filter((k) => answers[k] !== undefined && answers[k] !== null && answers[k] !== '')
    .map((k) => ({ key: k, label: labelStep(presetId, k), display: labelValue(presetId, answers[k]) }));

  return (
    <Card className="border-primary/30" data-testid="lead-ai-estimation-section">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg font-bold">
              {language === 'fr' ? 'Estimation IA' : 'AI Estimation'}
            </CardTitle>
            <Badge variant="secondary" className="text-xs">
              {PRESET_NAMES[presetId] || presetId}
            </Badge>
          </div>
          {lead.ai_estimation_result && (
            <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/40 hover:bg-amber-500/20" data-testid="lead-ai-estimation-result">
              {lead.ai_estimation_result}
            </Badge>
          )}
        </div>
        <CardDescription>
          {language === 'fr'
            ? 'Réponses du wizard IA collectées via le formulaire public. Prêtes pour le futur calculateur de prix.'
            : 'AI wizard answers collected through the public form. Ready for the upcoming price calculator.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Price range from the deterministic engine (fondation_drain, etc.) */}
        {lead.ai_estimation_price_range && (() => {
          const pr = lead.ai_estimation_price_range;
          return (
            <div className="space-y-2" data-testid="lead-ai-price-range">
              {pr.urgent && (
                <div className="rounded-lg border-2 border-red-500 bg-red-50 px-4 py-3">
                  <p className="font-bold text-red-700 text-sm" data-testid="lead-ai-urgent-flag">
                    🚨 URGENT — Infiltration d'eau active
                  </p>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3">
                {pr.min != null && pr.max != null ? (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {language === 'fr' ? 'Fourchette estimée' : 'Estimated range'}
                    </p>
                    <p className="text-xl font-black tabular-nums" data-testid="lead-ai-price-values">
                      {pr.min.toLocaleString('fr-CA')} $ – {pr.max.toLocaleString('fr-CA')} $
                      <span className="text-xs font-normal text-muted-foreground ml-1">CAD avant taxes</span>
                    </p>
                  </div>
                ) : (
                  <p className="text-sm font-semibold" data-testid="lead-ai-price-pending">
                    {pr.display_label || (language === 'fr' ? 'Prix à confirmer' : 'Price to confirm')}
                  </p>
                )}
                {pr.requires_validation && (
                  <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/40" data-testid="lead-ai-validation-badge">
                    {language === 'fr' ? 'Validation requise' : 'Validation required'}
                  </Badge>
                )}
                {pr.method && (
                  <Badge variant="outline" className="text-[10px]" data-testid="lead-ai-method-badge">
                    {language === 'fr' ? 'Méthode : ' : 'Method: '}{pr.method.replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
              {(pr.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="lead-ai-flags">
                  {pr.tags.map((tg) => (
                    <Badge key={tg} className="bg-red-500/10 text-red-700 border-red-500/30 text-[10px]">{tg}</Badge>
                  ))}
                </div>
              )}
              {(pr.notes || []).length > 0 && (
                <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5" data-testid="lead-ai-notes">
                  {pr.notes.map((n, i) => (<li key={i}>{n}</li>))}
                </ul>
              )}
            </div>
          );
        })()}

        {/* Answers grid */}
        {rows.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lead-ai-estimation-answers">
            {rows.map((row) => (
              <div
                key={row.key}
                className="p-3 rounded-lg border bg-muted/30"
                data-testid={`lead-ai-answer-${row.key}`}
              >
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  {row.label}
                </p>
                <p className="text-sm font-medium whitespace-pre-wrap break-words">{row.display}</p>
              </div>
            ))}
          </div>
        )}

        {/* Contact */}
        {Object.keys(contact).length > 0 && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              {language === 'fr' ? 'Coordonnées soumises' : 'Submitted contact'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="lead-ai-estimation-contact">
              {['full_name', 'phone', 'email', 'city'].filter((k) => contact[k]).map((k) => (
                <div key={k} className="p-2 rounded border bg-background">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.replace('_', ' ')}</p>
                  <p className="text-sm font-medium">{contact[k]}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photos */}
        <div>
          <div className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            <span>{language === 'fr' ? 'Photos jointes' : 'Attached photos'}</span>
            <Badge variant="outline" className="text-xs">{photoCount}</Badge>
          </div>
          {photoCount === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              {language === 'fr' ? 'Aucune photo soumise par le visiteur.' : 'No photos submitted.'}
            </p>
          ) : photos.length === 0 ? (
            <p className="text-xs text-muted-foreground italic" data-testid="lead-ai-photos-unavailable">
              {language === 'fr' ? 'Photos indisponibles (fichiers introuvables).' : 'Photos unavailable (files not found).'}
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="lead-ai-estimation-photos">
              {photos.map((p) => (
                <a
                  key={p.photo_id}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-square rounded-lg overflow-hidden border hover:shadow-md transition-shadow"
                  data-testid={`lead-ai-photo-${p.photo_id}`}
                >
                  <img src={p.url} alt={p.original_name || 'photo'} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
